import { NavLink, useSearchParams } from 'react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Shared header for the Reports area: title, the Health/Technicians tab
// switcher, and the Month/Quarter period selector. Period state lives in
// the URL (?granularity=&periodStart=) so reloading or sharing a link
// keeps the same view.

export type Granularity = 'month' | 'quarter';

export interface PeriodState {
  granularity: Granularity;
  periodStart: string; // YYYY-MM-DD, always the 1st of a month or quarter
  label: string;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function startOfQuarter(d: Date): Date {
  return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function labelFor(granularity: Granularity, start: Date): string {
  if (granularity === 'quarter') {
    return `${start.getFullYear()} Q${Math.floor(start.getMonth() / 3) + 1}`;
  }
  return start.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function parsePeriodStart(value: string | null, granularity: Granularity): Date {
  if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
    return granularity === 'quarter' ? startOfQuarter(dt) : startOfMonth(dt);
  }
  const now = new Date();
  return granularity === 'quarter' ? startOfQuarter(now) : startOfMonth(now);
}

export function usePeriod(): {
  period: PeriodState;
  setGranularity: (g: Granularity) => void;
  shift: (delta: -1 | 1) => void;
  goToCurrent: () => void;
} {
  const [params, setParams] = useSearchParams();
  const granularity: Granularity = params.get('granularity') === 'quarter' ? 'quarter' : 'month';
  const start = parsePeriodStart(params.get('periodStart'), granularity);

  function setGranularity(g: Granularity): void {
    const now = new Date();
    const newStart = g === 'quarter' ? startOfQuarter(now) : startOfMonth(now);
    const next = new URLSearchParams(params);
    next.set('granularity', g);
    next.set('periodStart', ymd(newStart));
    setParams(next, { replace: true });
  }

  function shift(delta: -1 | 1): void {
    const next = new Date(start);
    if (granularity === 'quarter') next.setMonth(next.getMonth() + delta * 3);
    else next.setMonth(next.getMonth() + delta);
    const params2 = new URLSearchParams(params);
    params2.set('granularity', granularity);
    params2.set('periodStart', ymd(next));
    setParams(params2, { replace: true });
  }

  function goToCurrent(): void {
    const now = new Date();
    const newStart = granularity === 'quarter' ? startOfQuarter(now) : startOfMonth(now);
    const next = new URLSearchParams(params);
    next.set('granularity', granularity);
    next.set('periodStart', ymd(newStart));
    setParams(next, { replace: true });
  }

  return {
    period: { granularity, periodStart: ymd(start), label: labelFor(granularity, start) },
    setGranularity,
    shift,
    goToCurrent,
  };
}

export function ReportsHeader({
  title,
  description,
}: {
  title: string;
  description: string;
}): React.ReactElement {
  const { period, setGranularity, shift, goToCurrent } = usePeriod();

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    [
      'inline-flex items-center rounded-[var(--radius-button)] px-3 py-1.5 text-sm font-medium transition',
      isActive
        ? 'bg-[var(--color-t3-blue)] text-white'
        : 'border border-[var(--color-border)] bg-white text-[var(--color-body)] hover:border-[var(--color-t3-blue)]',
    ].join(' ');

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-3xl font-bold text-[var(--color-header)]">{title}</h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">{description}</p>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <nav className="flex gap-2">
          <NavLink to={{ pathname: '/reports/health', search: window.location.search }} end className={tabClass}>
            Service health
          </NavLink>
          <NavLink to={{ pathname: '/reports/technicians', search: window.location.search }} end className={tabClass}>
            Technicians
          </NavLink>
          <NavLink to={{ pathname: '/reports/client-hours', search: window.location.search }} end className={tabClass}>
            Client hours
          </NavLink>
        </nav>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white text-sm">
            <button
              type="button"
              onClick={() => setGranularity('month')}
              className={
                'px-3 py-1.5 ' +
                (period.granularity === 'month'
                  ? 'bg-[var(--color-t3-blue)] text-white'
                  : 'text-[var(--color-body)]')
              }
            >
              Month
            </button>
            <button
              type="button"
              onClick={() => setGranularity('quarter')}
              className={
                'px-3 py-1.5 ' +
                (period.granularity === 'quarter'
                  ? 'bg-[var(--color-t3-blue)] text-white'
                  : 'text-[var(--color-body)]')
              }
            >
              Quarter
            </button>
          </div>
          <div className="inline-flex items-center gap-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-1 text-sm">
            <button
              type="button"
              onClick={() => shift(-1)}
              className="rounded p-1 text-[var(--color-muted)] hover:bg-[var(--color-surface)]"
              aria-label="Previous period"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[7rem] px-2 text-center font-medium">{period.label}</span>
            <button
              type="button"
              onClick={() => shift(1)}
              className="rounded p-1 text-[var(--color-muted)] hover:bg-[var(--color-surface)]"
              aria-label="Next period"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={goToCurrent}
            className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-1.5 text-xs text-[var(--color-muted)] hover:border-[var(--color-t3-blue)]"
          >
            Today
          </button>
        </div>
      </div>
    </div>
  );
}
